import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { LoginBackdrop } from '@/components/login/LoginBackdrop';
import {
  ChatThreadMock,
  CommandApprovalCard,
  FeatureGrid,
  PipelineSteps,
  StatBand
} from '@/components/login/appExamples';
import {
  CapabilityRow,
  Eyebrow,
  Headline,
  LOGIN_SUBCOPY,
  Mono,
  RavenWordmark
} from '@/components/login/loginContent';
import { buttonVariants } from '@/components/ui/button';

import { useAuth } from 'client-types/*';

function SectionHead({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="space-y-2">
      <Mono className="text-[11px] text-primary">{eyebrow}</Mono>
      <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        {title}
      </h2>
    </div>
  );
}

/**
 * Public landing page shown when an unauthenticated visitor opens the app. It
 * pitches Crimson Raven — workflow, capabilities and a live human-in-the-loop
 * example — with sign-in entry points in the header, hero, and a closing CTA
 * that route to /login.
 */
export default function Landing() {
  const { data: config, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user || (config && !config.requireLogin)) {
      navigate('/');
    }
  }, [config, user]);

  return (
    <div className="dark relative min-h-svh w-full bg-background text-foreground">
      <LoginBackdrop watermark="right" className="fixed" />

      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <RavenWordmark />
        <div className="flex items-center gap-5">
          <CapabilityRow className="hidden md:flex" />
          <Link
            to="/login"
            className={cn(buttonVariants({ size: 'sm' }), 'rounded-full px-5')}
          >
            Sign in
          </Link>
        </div>
      </header>

      <section className="relative z-10 mx-auto max-w-3xl px-6 pb-16 pt-16 text-center">
        <div className="flex justify-center">
          <Eyebrow />
        </div>
        <Headline className="mt-6 text-5xl sm:text-6xl xl:text-7xl" />
        <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
          {LOGIN_SUBCOPY}
        </p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/login"
            className={cn(buttonVariants({ size: 'lg' }), 'gap-2 px-6')}
          >
            Sign in
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/signup"
            className={cn(
              buttonVariants({ variant: 'outline', size: 'lg' }),
              'px-6'
            )}
          >
            Register
          </Link>
        </div>
        <CapabilityRow className="mt-10 justify-center" />
      </section>

      <section className="relative z-10 mx-auto max-w-6xl px-6 py-12">
        <SectionHead eyebrow="Workflow" title="How an engagement runs" />
        <PipelineSteps className="mt-6" />
      </section>

      <section className="relative z-10 mx-auto max-w-6xl px-6 py-12">
        <SectionHead eyebrow="Capabilities" title="What the copilot brings" />
        <FeatureGrid className="mt-6" />
      </section>

      <section className="relative z-10 mx-auto grid max-w-6xl items-center gap-8 px-6 py-12 lg:grid-cols-2">
        <div className="space-y-4">
          <SectionHead
            eyebrow="Human in the loop"
            title="You approve every move"
          />
          <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
            The copilot proposes; you decide. Each command is scored for risk
            and held for your allow, edit, or skip before it ever touches a
            target.
          </p>
          <ChatThreadMock className="max-w-md pt-2" />
        </div>
        <CommandApprovalCard className="lg:max-w-md lg:justify-self-end" />
      </section>

      <section className="relative z-10 mx-auto max-w-6xl px-6 py-12">
        <StatBand />
      </section>

      <section className="relative z-10 mx-auto max-w-6xl px-6 py-16">
        <div className="flex flex-col items-center gap-5 rounded-2xl border border-primary/30 bg-primary/[0.06] px-6 py-14 text-center">
          <Mono className="text-[11px] text-primary">
            Authorized operators only
          </Mono>
          <h2 className="max-w-2xl text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Ready to take command?
          </h2>
          <p className="max-w-md text-sm text-muted-foreground">
            The copilot does the work. You make the calls. Sign in to start an
            engagement.
          </p>
          <Link
            to="/login"
            className={cn(buttonVariants({ size: 'lg' }), 'mt-2 gap-2 px-7')}
          >
            Ready to take command?
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <footer className="relative z-10 mx-auto max-w-6xl px-6 py-10">
        <Mono className="block text-[10px] text-muted-foreground/70">
          Crimson Raven · Authorized operators only · Encrypted session
        </Mono>
      </footer>
    </div>
  );
}
